import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  X, DollarSign, RefreshCw, Plus, Trash2,
  Sparkles, ChevronDown, Calendar, Clock, Tag, MapPin,
  FileText, UserPlus, CheckCircle, Zap, Wand2, Users,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { toast } from 'react-toastify';
import CustomerIntake from './CustomerIntake';
import PeoplePicker from './PeoplePicker';
import AccountMark from './AccountMark';
import { byRecentAssignment, fmtLastWorked } from '../utils/people';

interface CreateJobModalProps {
  date: string;
  initialTime?: string;
  preCustomerId?: string;
  prePropertyId?: string;
  prefill?: {
    customer_id?: string;
    property_id?: string;
    time_start?: string;
    notes?: string;
    title?: string;
    service_type?: string;
  };
  onClose: () => void;
  onCreated: () => void;
}

interface LineItem {
  description: string;
  quantity: string;
  unit_price: string;
  is_optional: boolean;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const MOWING_TYPES = new Set(['mowing', 'fertilizer', 'aeration', 'seeding']);

const SERVICE_GROUPS = [
  {
    label: 'Mowing',
    color: 'green',
    options: [
      { value: 'mowing',     label: 'Mowing',     emoji: '🌿' },
      { value: 'fertilizer', label: 'Fertilizer',  emoji: '🧪' },
      { value: 'aeration',   label: 'Aeration',    emoji: '🌱' },
      { value: 'seeding',    label: 'Seeding',     emoji: '🌾' },
    ],
  },
  {
    label: 'Landscaping',
    color: 'blue',
    options: [
      { value: 'trimming',    label: 'Trimming',    emoji: '✂️' },
      { value: 'landscaping', label: 'Landscaping', emoji: '🏡' },
      { value: 'mulch',       label: 'Mulch',       emoji: '🪵' },
      { value: 'cleanup',     label: 'Cleanup',     emoji: '🧹' },
      { value: 'irrigation',  label: 'Irrigation',  emoji: '💧' },
      { value: 'fence',       label: 'Fence',       emoji: '🚧' },
      { value: 'sod',         label: 'Sod',         emoji: '🟩' },
      { value: 'drainage',    label: 'Drainage',    emoji: '🌊' },
    ],
  },
  {
    label: 'Other',
    color: 'gray',
    options: [
      { value: 'project', label: 'Project', emoji: '📋' },
      { value: 'other',   label: 'Other',   emoji: '⚙️' },
    ],
  },
  {
    label: 'No charge',
    color: 'gray',
    options: [
      { value: 'estimate', label: 'Quote / bid visit', emoji: '📋' },
      { value: 'fix',      label: 'Fix / redo (crew goes back, $0)', emoji: '🔧' },
    ],
  },
];

const ALL_SERVICES = SERVICE_GROUPS.flatMap(g => g.options);

const COMMON_LINE_ITEMS = [
  { name: 'Mowing',       description: 'Lawn mowing service' },
  { name: 'Mulching',     description: 'Mulch installation and spreading' },
  { name: 'Bush Trimming',description: 'Bush and shrub trimming' },
  { name: 'Bed Cleanup',  description: 'Garden bed cleanup and edging' },
  { name: 'Weed Removal', description: 'Hand weeding and weed control' },
  { name: 'Tree Trimming',description: 'Tree trimming and pruning' },
  { name: 'Plant Install',description: 'Plant installation' },
  { name: 'Rock Install', description: 'Rock and gravel installation' },
  { name: 'Fence Repair', description: 'Fence repair and maintenance' },
  { name: 'Aeration',     description: 'Lawn aeration service' },
  { name: 'Overseeding',  description: 'Lawn overseeding' },
  { name: 'Fertilizer',   description: 'Lawn fertilization treatment' },
];

const slugTag = (t: string) => t.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
function tagLabel(v: string) {
  const known = ALL_SERVICES.find(s => s.value === v);
  if (known) return `${known.emoji} ${known.label}`;
  return v.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ── Searchable service tags — the first tag is the job's main service ─────────
function ServiceTagPicker({ primary, tags, onChange }: {
  primary: string; tags: string[]; onChange: (primary: string, tags: string[]) => void;
}) {
  const [q, setQ] = useState('');
  const selected = Array.from(new Set([primary, ...tags].filter(Boolean)));
  const term = q.trim().toLowerCase();
  const pool = ALL_SERVICES.filter(s => !selected.includes(s.value));
  const matches = term ? pool.filter(s => s.label.toLowerCase().includes(term) || s.value.includes(term)) : pool;
  const customValue = term.length >= 2 ? slugTag(term) : '';
  const showCustom = !!customValue
    && !ALL_SERVICES.some(s => s.value === customValue || s.label.toLowerCase() === term)
    && !selected.includes(customValue);

  function commit(next: string[]) { onChange(next[0] || '', next.slice(1)); }
  function add(v: string) { if (!v) return; commit(selected.includes(v) ? selected : [...selected, v]); setQ(''); }
  function remove(v: string) { commit(selected.filter(t => t !== v)); }
  function makePrimary(v: string) { commit([v, ...selected.filter(t => t !== v)]); }

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[1.75rem]">
        {selected.length === 0 && <span className="text-xs text-gray-500 self-center">Nothing picked yet — search below.</span>}
        {selected.map((v, i) => (
          <span key={v} className={`press inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-xs font-medium border ${i === 0 ? 'bg-brand-600 text-black border-brand-600' : 'bg-emerald-50 text-emerald-800 border-emerald-200'}`}>
            <button type="button" onClick={() => i !== 0 && makePrimary(v)} title={i === 0 ? 'Main service' : 'Make this the main service'}>
              {tagLabel(v)}{i === 0 ? ' · main' : ''}
            </button>
            <button type="button" onClick={() => remove(v)} className="rounded-full p-0.5 hover:bg-black/10" title="Remove"><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <input
        value={q}
        onChange={e => setQ(e.target.value)}
        onKeyDown={e => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          if (matches[0]) add(matches[0].value); else if (showCustom) add(customValue);
        }}
        placeholder="Search services — mulch, trimming, irrigation… (Enter adds)"
        className="input text-sm"
      />
      <div className="flex flex-wrap gap-1.5 mt-2">
        {matches.slice(0, 14).map(s => (
          <button key={s.value} type="button" onClick={() => add(s.value)}
            className="px-2.5 py-1 text-xs rounded-full border bg-white text-gray-600 border-gray-200 hover:border-brand-400 hover:text-brand-600">
            {s.emoji} {s.label}
          </button>
        ))}
        {showCustom && (
          <button type="button" onClick={() => add(customValue)}
            className="px-2.5 py-1 text-xs rounded-full border border-dashed border-brand-300 text-brand-700 bg-brand-50">
            + add "{q.trim()}"
          </button>
        )}
        {term && matches.length === 0 && !showCustom && <span className="text-xs text-gray-500 self-center">No match</span>}
      </div>
      {selected.length > 1 && <p className="text-[11px] text-gray-500 mt-1.5">Tap a tag to make it the main service.</p>}
    </div>
  );
}

// ── Inline Add-Customer form ──────────────────────────────────────────────────
// The same questions as Customers → New (components/CustomerIntake).
function AddCustomerInline({ prefillName, onAdded, onCancel }: {
  prefillName: string;
  onAdded: (customer: any, property: any) => void;
  onCancel: () => void;
}) {
  return (
    <div className="mt-2 border border-brand-200 rounded-xl bg-brand-50/60 p-3">
      <div className="flex items-center gap-1.5 mb-2">
        <UserPlus className="h-3.5 w-3.5 text-brand-600" />
        <p className="text-xs font-semibold text-brand-700 uppercase tracking-wide">New Customer</p>
        <button onClick={onCancel} className="ml-auto text-gray-500 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>
      </div>
      <CustomerIntake compact source="job" prefill={{ name: prefillName }} onCancel={onCancel} submitLabel="Add customer"
        onSaved={out => { toast.success(`${out.customer.name} added`); onAdded(out.customer, out.property || null); }} />
    </div>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────────
export default function CreateJobModal({
  date, initialTime = '', preCustomerId = '', prePropertyId = '',
  prefill, onClose, onCreated,
}: CreateJobModalProps) {
  const qc = useQueryClient();
  const [customerSearch, setCustomerSearch]         = useState('');
  const [showAddCustomer, setShowAddCustomer]       = useState(false);
  const [showCustomerDrop, setShowCustomerDrop]     = useState(false);
  const [lineItems, setLineItems]                   = useState<LineItem[]>([]);
  const [aiLoadingIdx, setAiLoadingIdx]             = useState<number | null>(null);
  const [showLineItemDropdown, setShowLineItemDropdown] = useState(false);
  const [showAiTextInput, setShowAiTextInput]       = useState(false);
  const [aiTextInput, setAiTextInput]               = useState('');
  const [aiParsing, setAiParsing]                   = useState(false);
  const [aiQuoteText, setAiQuoteText]               = useState('');
  const [showRecurring, setShowRecurring]           = useState(false);
  // "Monthly" is every 4 weeks (custom, 28 days). The day box only opens for Custom.
  const [customOpen, setCustomOpen]                 = useState(false);
  const navigate = useNavigate();
  const [showOpenShift, setShowOpenShift]           = useState(false);
  // Who an open shift is offered to (owner, Sep 30 2026: "for open shift let me
  // choose select people who I want or groups of people"). Anyone on the crew
  // app is how it always worked; picked people are the only ones who see it,
  // can claim it and get the text.
  const [shiftTo, setShiftTo]                       = useState<'everyone' | 'picked'>('everyone');
  const [shiftPicks, setShiftPicks]                 = useState<number[]>([]);
  const [textShiftNow, setTextShiftNow]             = useState(true);
  // A mowing stop put on a route is almost never a one-off — the route exists
  // because it comes round again. Asking here saves setting it up afterwards.
  const [routeCadence, setRouteCadence] = useState<'' | 'weekly' | 'biweekly' | 'monthly'>('');
  // Every job starts with a choice: AI builds it from pasted notes, or manual.
  const [mode, setMode] = useState<'choose' | 'ai' | 'form'>(prefill ? 'form' : 'choose');
  const [aiJobText, setAiJobText] = useState('');
  const [aiJobBusy, setAiJobBusy] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  // Employees picked at creation (job_crew on the job / every visit of a schedule)
  const [crewIds, setCrewIds] = useState<number[]>([]);
  // Multi-day work: this job is day 2 (3, …) of an earlier job for the same client
  const [continueOf, setContinueOf] = useState<any | null>(null);
  const [continueDismissed, setContinueDismissed] = useState(false);
  // Multi-day work: "takes N days" → one linked job per day after the first
  const [spanDays, setSpanDays] = useState(1);

  const startDay = new Date(date + 'T12:00:00').getDay();

  const [timeOpen, setTimeOpen] = useState(false);
  const [form, setForm] = useState({
    customer_id:              prefill?.customer_id || preCustomerId,
    property_id:              prefill?.property_id || prePropertyId,
    scheduled_date:           date,
    // No time picked = anytime that day (owner, Sep 25 2026: "not have to put it at 8 o'clock").
    time_start:               prefill?.time_start || initialTime || '',
    notes:                    prefill?.notes || '',
    title:                    prefill?.title || '',
    service_type:             prefill?.service_type || 'mowing',
    route_id:                 '',
    open_shift_required_count: 1,
    is_open_shift:            false,
    is_first_time:            false,
    minutes_override:         '',
    time_is_firm:             false,
    service_tags:             [] as string[],
    crew_notes:               '',
    flat_crew_pay:            '',
    // Recurring
    is_recurring:             false,
    frequency:                'weekly' as 'weekly' | 'biweekly' | 'custom',
    selected_days:            [startDay] as number[],
    custom_interval_days:     14,
    end_type:                 'date' as 'never' | 'date' | 'count',
    end_date:                 '',
    end_after_occurrences:    10,
  });

  const { data: customers = [] } = useQuery('customers-list', () =>
    api.get('/customers').then(r => r.data.customers as any[])
  );
  // Which routes are actually out on the date they picked. Asked of the jobs
  // themselves rather than a route's nominal day, because the truth about what
  // runs on a given Tuesday is which stops are booked for that Tuesday.
  const { data: thatDaysJobs = [] } = useQuery(
    ['jobs-on-date', form.scheduled_date],
    () => api.get('/jobs', { params: { date: form.scheduled_date } }).then(r => r.data.jobs as any[]),
    { enabled: !!form.scheduled_date });

  const { data: routes = [] } = useQuery('routes-list', () =>
    api.get('/routes').then(r => r.data.routes as any[])
  );
  const { data: settingsData } = useQuery('settings', () =>
    api.get('/settings').then(r => r.data.settings as Record<string, string>)
  );
  const { data: catalogData = [] } = useQuery('service-catalog', () =>
    api.get('/service-catalog').then(r => r.data.items as any[])
  );
  // Every property, so the client box also finds "204 Choke Canyon" directly.
  const { data: allProps = [] } = useQuery('properties-all', () =>
    api.get('/properties').then(r => r.data.properties as any[])
  );
  const { data: employeesData = [] } = useQuery('employees', () =>
    api.get('/employees').then(r => r.data.employees as any[])
  );
  // Recent non-mowing work for the picked client → "is this day 2 of that?"
  const { data: recentJobs = [] } = useQuery(
    ['cust-recent-jobs', form.customer_id],
    () => api.get('/jobs', { params: { customer_id: form.customer_id } }).then(r => r.data.jobs as any[]),
    { enabled: !!form.customer_id }
  );
  // A bid this client has open or just accepted and nobody has booked yet → offer it, instead of retyping it as a job
  // (owner's list: "when adding a job, if the client had a bid/quote recently then recommend that as the job").
  // The server decides which (services/jobBidSuggest): the last 90 days, drafts too, a bid on a duplicate row of the
  // same person, and a bid that became Project Work with no job yet (Vicki's "Project 4" was missing, Oct 20 2026).
  const { data: custBids = [] } = useQuery(
    ['cust-recent-bids', form.customer_id],
    () => api.get('/quotes', { params: { customer_id: form.customer_id, for_job: 1 } }).then(r => (r.data.quotes || []) as any[]),
    { enabled: !!form.customer_id, staleTime: 60_000 }
  );
  const recentBids = React.useMemo(() => (form.customer_id ? (custBids as any[]).slice(0, 3) : []), [custBids, form.customer_id]);
  const bidBadge = (q: any) => {
    const why = q.offer?.why || q.status;
    if (why === 'project') return { words: `project #${q.offer?.project_id || q.converted_to_project_id} · not on a day yet`, cls: 'bg-indigo-100 text-indigo-700' };
    if (why === 'accepted') return { words: 'accepted', cls: 'bg-green-100 text-green-700' };
    if (why === 'draft') return { words: 'draft · not sent yet', cls: 'bg-gray-100 text-gray-600' };
    return { words: 'sent', cls: 'bg-gray-100 text-gray-600' };
  };

  const continueCandidates = React.useMemo(() => {
    if (!form.customer_id || continueOf || continueDismissed) return [];
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
    const floor = new Date(today + 'T12:00:00Z'); floor.setUTCDate(floor.getUTCDate() - 21);
    const floorISO = floor.toISOString().slice(0, 10);
    return (recentJobs as any[])
      .filter((j: any) => {
        const d = String(j.scheduled_date || '').slice(0, 10);
        return d >= floorISO && d <= today && ['scheduled', 'in_progress', 'complete'].includes(j.status)
          && (String(j.service_type || '').toLowerCase() !== 'mowing' || j.is_partial);
      })
      .sort((a: any, b: any) => String(b.scheduled_date).localeCompare(String(a.scheduled_date)))
      .slice(0, 3);
  }, [recentJobs, form.customer_id, continueOf, continueDismissed]);

  function pickContinuation(j: any) {
    setContinueOf(j);
    const tags = String(j.service_tags || '').split(',').filter(Boolean);
    const primary = j.service_type || tags[0] || '';
    setForm(p => ({
      ...p,
      property_id: j.property_id ? String(j.property_id) : p.property_id,
      service_type: primary,
      service_tags: tags.filter((t: string) => t !== primary),
      title: j.title ? `${j.title} — day 2` : `${(primary || 'Job').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())} — day 2`,
      crew_notes: j.crew_notes_snapshot || p.crew_notes,
      notes: `Day 2 of job #${j.id} (${String(j.scheduled_date).slice(0, 10)}) — billed there`,
      route_id: '',
    }));
    setLineItems([]);
    toast.info(`Linked as day 2 of job #${j.id} — no charge here unless you add line items`);
  }

  const settings     = settingsData || {} as Record<string, string>;
  const avoidSundays = settings.avoid_sundays === 'true';

  // Switching a job to mowing drops the quote lines and the flat pay, so what
  // is on screen and what gets sent never disagree.
  useEffect(() => {
    if (!MOWING_TYPES.has(form.service_type)) return;
    setLineItems(v => (v.length ? [] : v));
    setForm(p => (p.flat_crew_pay === '' ? p : { ...p, flat_crew_pay: '' }));
  }, [form.service_type]);

  useEffect(() => {
    if (form.service_type === 'mowing' && settings.mowing_season_end && !form.end_date) {
      const [m, d] = settings.mowing_season_end.split('-');
      const yr = new Date().getFullYear();
      setForm(p => ({ ...p, end_type: 'date', end_date: `${yr}-${m.padStart(2,'0')}-${d.padStart(2,'0')}` }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.service_type, settings.mowing_season_end]);

  useEffect(() => {
    if (avoidSundays) {
      setForm(p => ({ ...p, selected_days: p.selected_days.filter(d => d !== 0) }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avoidSundays]);

  const { data: propData } = useQuery(
    ['props-for', form.customer_id],
    () => form.customer_id
      ? api.get('/properties', { params: { customer_id: form.customer_id } }).then(r => r.data.properties as any[])
      : Promise.resolve([]),
    { enabled: !!form.customer_id }
  );
  const properties = propData || [];

  useEffect(() => {
    if (form.customer_id && properties.length > 0 && !form.property_id) {
      setForm(p => ({ ...p, property_id: String(properties[0].id) }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties, form.customer_id]);

  // A mow is charged at the yard's price. A yard with none used to get "add one on the property"
  // with nowhere to add it (owner, Sep 19 2026: "I can't finish creating the job") — it is typed here and saved on the yard.
  const [yardPrice, setYardPrice] = useState('');
  const selectedCustomer = (customers as any[]).find((c: any) => String(c.id) === form.customer_id);
  const selectedProp     = properties.find((p: any) => String(p.id) === form.property_id);
  const lineItemsTotal   = lineItems.reduce((s, li) => s + (parseFloat(li.unit_price) || 0) * (parseFloat(li.quantity) || 1), 0);
  const isMowing         = MOWING_TYPES.has(form.service_type);
  // One-off work goes on the calendar only from a bid with a written scope the customer signed (owner, Nov 1 2026;
  // server services/oneOffGates — the same rule, refused there too). The modal has no bid to hang it off, so it
  // says so and points at the bids. Settings → Bids switches the rule off.
  const scopeGateOn      = !['0', 'false', 'off', 'no'].includes(String(settings.one_off_scope_gate ?? '1').trim().toLowerCase());
  const oneOffNeedsBid   = scopeGateOn && !form.is_recurring && !continueOf
    && !['mowing', 'todo', 'estimate', 'fix', 'bonus', ''].includes(String(form.service_type || '').toLowerCase())
    && !/mow/i.test(String(form.service_type || ''));
  // Open shift is for one-off, non-mowing work: a repeating series always books
  // its visits closed, and a route stop is covered by whoever runs the route.
  const openShiftAllowed = !isMowing && !form.is_recurring;
  const isOpenShift      = form.is_open_shift && openShiftAllowed;
  const shiftNeedsPeople = isOpenShift && shiftTo === 'picked' && shiftPicks.length === 0;

  // Switching to mowing or turning Recurring on used to hide the section and
  // quietly leave the job marked open. Clear it instead.
  useEffect(() => {
    if (openShiftAllowed) return;
    setShowOpenShift(false);
    setForm(p => (p.is_open_shift ? { ...p, is_open_shift: false } : p));
  }, [openShiftAllowed]);

  const create = useMutation(async () => {
    if (!form.customer_id) throw new Error('Customer is required');
    if (!form.scheduled_date) throw new Error('Date is required');

    // A continuation is billed on day 1: no price here unless line items were added.
    // A mow on a yard with no price yet takes the price typed on this form, and the yard keeps it.
    const typedYardPrice = isMowing && !continueOf && !(Number(selectedProp?.mowing_price) > 0) && Number(yardPrice) > 0 ? Math.round(Number(yardPrice) * 100) / 100 : null;
    const effectivePriceSnap = (!isMowing && lineItems.length > 0) ? lineItemsTotal : (continueOf ? 0 : typedYardPrice ?? undefined);
    let notify: any = null;
    // What the server said about the job it made — who came off it when it went onto an offered day.
    let said: string | null = null;

    const payload: any = {
      customer_id: Number(form.customer_id),
      property_id: form.property_id ? Number(form.property_id) : null,
      crew_id: null,
      assigned_crew_member_id: null,
      scheduled_date: form.scheduled_date,
      time_start: form.time_start || null,
      time_end: null,
      notes: form.notes || null,
      is_open_shift: isOpenShift,
      open_shift_required_count: isOpenShift ? (form.open_shift_required_count || 1) : 1,
      ...(isOpenShift ? {
        open_shift_to: shiftTo,
        ...(shiftTo === 'picked' ? { invited_crew_member_ids: shiftPicks } : {}),
        notify_open_shift: textShiftNow,
      } : {}),
      title: form.title || null,
      service_type: form.service_type || null,
      service_tags: Array.from(new Set([form.service_type, ...form.service_tags].filter(Boolean))),
      time_is_firm: form.time_start && form.time_is_firm ? 1 : 0,
      time_anytime: !form.time_start,
      crew_notes: form.crew_notes.trim() || null,
      ...(form.flat_crew_pay !== '' ? { crew_pay_override: Number(form.flat_crew_pay), crew_pay_is_manual: true } : {}),
      route_id: isMowing && form.route_id ? Number(form.route_id) : null,
      is_first_time: isMowing && form.is_first_time ? 1 : 0,
      crew_member_ids: crewIds,
      continues_job_id: continueOf ? continueOf.id : null,
      ...(effectivePriceSnap !== undefined ? { price_snapshot: effectivePriceSnap } : {}),
      ...(typedYardPrice && form.property_id ? { save_to_property: true } : {}),
      ...(form.minutes_override !== '' ? { estimated_minutes_snapshot: Number(form.minutes_override) } : {}),
      ...(!isMowing && lineItems.length > 0 ? { line_items: lineItems.map(li => ({
        description: li.description,
        quantity: parseFloat(li.quantity) || 1,
        unit_price: parseFloat(li.unit_price) || 0,
        is_optional: li.is_optional,
      })) } : {}),
    };

    if (form.is_recurring) {
      const multiDay = form.selected_days.length > 1;
      // A repeating mow reads its price off the yard, so the yard gets it first.
      if (typedYardPrice && form.property_id) await api.put(`/properties/${form.property_id}`, { mowing_price: typedYardPrice });
      const made = await api.post('/recurring-schedules', {
        customer_id: payload.customer_id,
        property_id: payload.property_id,
        crew_id: null,
        assigned_crew_member_id: null,
        frequency: form.frequency,
        day_of_week: multiDay ? null : (form.selected_days[0] ?? startDay),
        days_of_week: multiDay ? form.selected_days.sort((a, b) => a - b).join(',') : null,
        custom_interval_days: form.frequency === 'custom' ? form.custom_interval_days : null,
        end_date: form.end_type === 'date' ? form.end_date || null : null,
        end_after_occurrences: form.end_type === 'count' ? form.end_after_occurrences : null,
        time_start: form.time_start || null,
        service_type: form.service_type || 'mowing',
        route_id: isMowing && form.route_id ? Number(form.route_id) : null,
        crew_member_ids: crewIds,
        // The date on the form is the first visit. Without it the series
        // started "this week" — sometimes today, mid-morning, with no crew.
        start_date: form.scheduled_date || undefined,
      });
      // The route was not out on that date (another weekday, or its off week): the server says what it booked instead.
      return { routeDayNote: (made.data?.route_day?.message || null) as string | null, notify, said };
    } else {
      const { data } = await api.post('/jobs', payload);
      said = data?.message || null;
      // "Text them now" on an open shift: the server texted them (or, with
      // texting not set up, wrote the messages). Drafts wait for the job page.
      notify = data?.notify || null;
      if (notify?.drafts?.length && data?.job?.id) qc.setQueryData(['open-shift-drafts', String(data.job.id)], notify.drafts);
      if (spanDays > 1 && data?.job?.id) {
        await api.post(`/jobs/${data.job.id}/span`, { days: spanDays, skip_weekends: true });
      }
      // A mowing stop on a route that comes round again. The visit just booked
      // becomes number one of the series, so nothing is duplicated on its date
      // and the cadence walks on from there.
      if (isMowing && form.route_id && routeCadence && data?.job?.id) {
        const day = new Date(`${form.scheduled_date}T12:00:00`).getDay();
        const made = await api.post('/recurring-schedules', {
          customer_id: payload.customer_id,
          property_id: payload.property_id,
          frequency: routeCadence === 'monthly' ? 'custom' : routeCadence,
          // Monthly on a route means every fourth visit, not the 3rd of the
          // month — four weeks keeps it on the same day the route runs.
          custom_interval_days: routeCadence === 'monthly' ? 28 : null,
          day_of_week: day,
          time_start: form.time_start || null,
          service_type: form.service_type || 'mowing',
          route_id: Number(form.route_id),
          anchor_date: form.scheduled_date,
          start_date: form.scheduled_date,
          attach_job_id: data.job.id,
        });
        // The first mow was not on the route's day: the server kept it a one-off and put the repeat on the route's own day. Say so.
        return { routeDayNote: (made.data?.route_day?.message || null) as string | null, notify, said };
      }
    }
    return { routeDayNote: null as string | null, notify, said };
  }, {
    onSuccess: ({ routeDayNote, notify, said }) => {
      toast.success(
        routeDayNote ? routeDayNote
        : form.is_recurring ? 'Recurring schedule created!'
        : (isMowing && form.route_id && routeCadence)
          ? `On the route, ${routeCadence === 'monthly' ? 'every 4 weeks' : routeCadence === 'biweekly' ? 'every 2 weeks' : 'weekly'} from ${form.scheduled_date}`
          : 'Job created!');
      if (notify?.drafts?.length) toast.info("Texting isn't set up — open the job to copy the messages", { autoClose: 8000 });
      else if (notify?.message) toast.info(notify.message);
      // The people he just picked came off it (it went onto a day offered as an open shift): never without a word.
      if (said) toast.info(said, { autoClose: 10000 });
      onCreated();
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || e.message || 'Failed to create job'); },
  });

  const f = (k: string) => (e: React.ChangeEvent<any>) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const freqSummary = () => {
    if (form.frequency === 'custom') return !customOpen && Number(form.custom_interval_days) === 28 ? 'Monthly (every 4 weeks)' : `Every ${form.custom_interval_days} days`;
    if (!form.selected_days.length) return '';
    const dayNames = [...form.selected_days].sort((a, b) => a - b).map(d => DAYS[d]).join(', ');
    return `${form.frequency === 'weekly' ? 'Weekly' : 'Every 2 weeks'} on ${dayNames}`;
  };

  // Customer search filtering
  const trimmed = customerSearch.trim();
  const filteredCustomers = trimmed
    ? (customers as any[]).filter((c: any) =>
        (c.display_name || c.name || '').toLowerCase().includes(trimmed.toLowerCase()) ||
        (c.company_name || '').toLowerCase().includes(trimmed.toLowerCase())
      )
    : (customers as any[]).slice().sort((a: any, b: any) => b.id - a.id);
  const propMatches = trimmed.length >= 2
    ? (allProps as any[]).filter((p: any) =>
        [p.address, p.label, p.city, p.zip_code].filter(Boolean)
          .some((v: any) => String(v).toLowerCase().includes(trimmed.toLowerCase())))
      .slice(0, 8)
    : [];
  const noMatch = trimmed.length >= 2 && filteredCustomers.length === 0 && propMatches.length === 0;

  function selectProperty(p: any) {
    setForm(prev => ({ ...prev, customer_id: String(p.customer_id), property_id: String(p.id) }));
    setCustomerSearch('');
    setShowCustomerDrop(false);
    setShowAddCustomer(false);
  }

  function selectCustomer(c: any) {
    setForm(p => ({ ...p, customer_id: String(c.id), property_id: '' }));
    setCustomerSearch('');
    setShowCustomerDrop(false);
    setShowAddCustomer(false);
  }

  function handleCustomerAdded(customer: any, property: any) {
    qc.invalidateQueries('customers-list');
    setForm(p => ({
      ...p,
      customer_id: String(customer.id),
      property_id: property ? String(property.id) : '',
    }));
    setCustomerSearch('');
    setShowAddCustomer(false);
    setShowCustomerDrop(false);
  }

  function addLineItem(preset?: typeof COMMON_LINE_ITEMS[0] | any) {
    const catalogItem = preset?.id ? preset : null;
    const commonItem  = preset?.name && !preset?.id ? preset : null;
    setLineItems(prev => [...prev, {
      description: catalogItem?.name || commonItem?.name || '',
      quantity:    '1',
      unit_price:  catalogItem?.default_unit_price ? String(catalogItem.default_unit_price) : '',
      is_optional: false,
    }]);
  }

  function updateLineItem(idx: number, field: keyof LineItem, value: string | boolean) {
    setLineItems(prev => prev.map((li, i) => i === idx ? { ...li, [field]: value } : li));
  }

  function removeLineItem(idx: number) {
    setLineItems(prev => prev.filter((_, i) => i !== idx));
  }

  async function applyAiText() {
    const raw = aiTextInput.trim();
    if (!raw) return;
    setAiParsing(true);
    try {
      // The AI builds a full itemized quote in the house style: numbered
      // sections, FREE lines, OR-priced alternatives, options, scenario totals.
      const r = await api.post('/ai/itemize-quote', { text: raw, service_type: form.service_type });
      const items: LineItem[] = (r.data.line_items || [])
        .filter((li: any) => li.description)
        .map((li: any) => ({
          description: `${li.section != null ? `${li.section}. ` : ''}${li.description}`,
          quantity: String(li.quantity || 1),
          unit_price: li.unit_price == null ? '' : String(li.unit_price),
          is_optional: !!li.optional,
        }));
      if (items.length > 0) setLineItems(prev => [...prev.filter(p => p.description || p.unit_price), ...items]);
      setAiQuoteText(r.data.customer_text || '');
      setAiTextInput('');
      toast.success(`${items.length} line items built — quote text ready to copy`);
    } catch (e: any) {
      // AI unavailable? Fall back to the simple "description price" parser.
      const segments = raw.split(/[,;]+/).map(s => s.trim()).filter(Boolean);
      const parsed: LineItem[] = segments.map(seg => {
        const m = seg.match(/^(.+?)\s+\$?(\d+(?:\.\d{1,2})?)$/);
        if (m) return { description: m[1].trim(), quantity: '1', unit_price: m[2], is_optional: false };
        return { description: seg, quantity: '1', unit_price: '', is_optional: false };
      });
      setLineItems(prev => [...prev, ...parsed]);
      setAiTextInput('');
      setShowAiTextInput(false);
      toast.info(e.response?.data?.message ? `AI unavailable (${e.response.data.message}) — items parsed simply` : 'AI unavailable — items parsed simply');
    } finally { setAiParsing(false); }
  }

  async function generateAiDescription(idx: number) {
    const li = lineItems[idx];
    if (!li.description) { toast.error('Enter an item name first'); return; }
    setAiLoadingIdx(idx);
    try {
      const r = await api.post('/ai/describe-line-item', { title: li.description, service_type: form.service_type });
      updateLineItem(idx, 'description', r.data.description || li.description);
      toast.success('Description ready');
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'AI not available');
    } finally { setAiLoadingIdx(null); }
  }

  const currentServiceOpt = ALL_SERVICES.find(s => s.value === form.service_type);
  const activeRoutes = (routes as any[]).filter((r: any) => r.is_active !== 0);

  /**
   * How often a route is out, read off its own calendar: the days between its next two real runs (run_days —
   * dates with two stops or more), else what most of its yards are on. "Add to Monday Week A" used to tick
   * Weekly whatever the route was; since Oct 5 2026 every route is out every other week, and a yard put on one
   * as weekly by a slip sat alone on it in the off weeks. Weekly stays one tap away — and a weekly yard then
   * rides both routes of the day.
   */
  const cadenceOfRoute = (id: number): '' | 'weekly' | 'biweekly' | 'monthly' => {
    const r: any = (routes as any[]).find((x: any) => x.id === id);
    const runs: string[] = Array.isArray(r?.run_days) ? r.run_days : [];
    const gap = runs.length >= 2 ? Math.round((Date.parse(`${runs[1]}T12:00:00Z`) - Date.parse(`${runs[0]}T12:00:00Z`)) / 86400000) : Number(r?.interval_days) || 0;
    return gap >= 28 ? 'monthly' : gap >= 14 ? 'biweekly' : 'weekly';
  };
  /** Is the picked route really out on the picked date? null when its calendar cannot say (beyond the runs on file). */
  const pickedRouteOut = React.useMemo(() => {
    const r: any = (routes as any[]).find((x: any) => String(x.id) === String(form.route_id));
    const runs: string[] = Array.isArray(r?.run_days) ? r.run_days : [];
    if (!r || !form.scheduled_date || !runs.length || form.scheduled_date > runs[runs.length - 1] || form.scheduled_date < runs[0]) return null;
    return runs.includes(form.scheduled_date);
  }, [routes, form.route_id, form.scheduled_date]);
  // A route offered as an open shift is one day's work: a visit put on it is booked once and goes with whoever
  // claims the day. Nothing repeats on it (the server refuses a schedule there), so "Add to …" must not tick Weekly.
  const pickedRouteOffered = Number((routes as any[]).find((x: any) => String(x.id) === String(form.route_id))?.open_shift) === 1;
  useEffect(() => { if (pickedRouteOffered && routeCadence) setRouteCadence(''); }, [pickedRouteOffered, routeCadence]);

  /** The picked route's next run after the picked date, off the same runs — named in the hint instead of "its next run". */
  const pickedRouteNext = React.useMemo(() => {
    const r: any = (routes as any[]).find((x: any) => String(x.id) === String(form.route_id));
    const runs: string[] = Array.isArray(r?.run_days) ? r.run_days : [];
    return (form.scheduled_date && runs.find(d => d > form.scheduled_date)) || null;
  }, [routes, form.route_id, form.scheduled_date]);
  /** 2026-10-19 → "Mon Oct 19" — a date is never shown raw. */
  const niceDay = (d: string) => (/^\d{4}-\d{2}-\d{2}/.test(String(d || ''))
    ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).replace(',', '') : String(d || ''));

  /** Routes with stops booked on the chosen date, busiest first. */
  const routesThatDay = React.useMemo(() => {
    const by = new Map<number, { id: number; name: string; stops: number }>();
    for (const j of (thatDaysJobs as any[])) {
      if (!j.route_id) continue;
      const cur = by.get(j.route_id) || { id: j.route_id, name: j.route_name || `Route ${j.route_id}`, stops: 0 };
      cur.stops++; by.set(j.route_id, cur);
    }
    return Array.from(by.values()).sort((a, b) => b.stops - a.stops);
  }, [thatDaysJobs]);

  // Fill the form from the AI's job draft, then hand over for review.
  async function buildJobWithAi() {
    const raw = aiJobText.trim();
    if (!raw) return;
    setAiJobBusy(true);
    try {
      const r = await api.post('/ai/create-job', { text: raw });
      const d = r.data || {};
      setForm(p => ({
        ...p,
        customer_id: d.matched_customer_id ? String(d.matched_customer_id) : p.customer_id,
        title: d.title || p.title,
        service_type: d.service_type || p.service_type,
        scheduled_date: d.scheduled_date || p.scheduled_date,
        time_start: d.time_start || p.time_start,
        notes: d.notes || p.notes,
        crew_notes: d.crew_notes || p.crew_notes,
        time_is_firm: !!d.time_is_firm,
        service_tags: Array.isArray(d.service_tags) ? d.service_tags.filter((t: string) => t && t !== d.service_type) : p.service_tags,
        minutes_override: d.estimated_minutes ? String(d.estimated_minutes) : p.minutes_override,
      }));
      const items: LineItem[] = (d.line_items || [])
        .filter((li: any) => li.description && li.unit_price != null)
        .map((li: any) => ({
          description: li.description,
          quantity: String(li.quantity || 1),
          unit_price: String(li.unit_price),
          is_optional: false,
        }));
      if (items.length) setLineItems(items);
      if (d.matched_customer_id) {
        const c = (customers as any[]).find((x: any) => String(x.id) === String(d.matched_customer_id));
        toast.success(`Draft ready for ${c?.name || 'customer'} — review and save`);
      } else if (d.new_customer?.name) {
        toast.info(`Draft ready — "${d.new_customer.name}" looks new: pick or create the customer below`);
        setCustomerSearch(d.new_customer.name);
      } else {
        toast.info('Draft ready — pick the customer below');
      }
      setMode('form');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'AI could not build the job — try again or go manual');
    } finally { setAiJobBusy(false); }
  }

  // ── Step 0: AI or manual? ──────────────────────────────────────────────────
  if (mode === 'choose' || mode === 'ai') {
    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/40">
        <div className="bg-white w-full sm:rounded-2xl shadow-2xl sm:max-w-lg max-h-[92vh] flex flex-col rounded-t-2xl">
          <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100">
            <h2 className="text-lg font-black uppercase tracking-tight text-gray-900">New Job</h2>
            <button onClick={onClose} className="p-1.5 text-gray-500 hover:text-gray-600 hover:bg-gray-100 rounded-full">
              <X className="h-5 w-5" />
            </button>
          </div>
          {mode === 'choose' ? (
            <div className="p-5 space-y-3">
              <button onClick={() => setMode('ai')}
                className="w-full text-left p-4 rounded-2xl border-2 border-purple-200 bg-purple-50/60 hover:border-purple-400 transition-colors">
                <p className="font-bold text-purple-800 flex items-center gap-2"><Wand2 className="h-5 w-5" /> Build with AI</p>
                <p className="text-sm text-purple-700/80 mt-1">Paste your notes or the customer's texts — AI fills in the customer, date, work, and prices for you to review.</p>
              </button>
              <button onClick={() => setMode('form')}
                className="w-full text-left p-4 rounded-2xl border-2 border-gray-200 hover:border-brand-400 transition-colors">
                <p className="font-bold text-gray-800 flex items-center gap-2"><Plus className="h-5 w-5" /> Manual</p>
                <p className="text-sm text-gray-500 mt-1">Fill out the job form yourself.</p>
              </button>
            </div>
          ) : (
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-600">Paste anything — walkthrough notes, the customer's message, a voicemail transcript. AI drafts the whole job; you review before saving.</p>
              <textarea autoFocus value={aiJobText} onChange={e => setAiJobText(e.target.value)} rows={7}
                placeholder={'"Jeff Russell wants a fall cleanup next Friday morning, leaves + beds, agreed on 180. Gate code 4482."'}
                className="input text-sm w-full resize-none" />
              <div className="flex gap-2">
                <button onClick={buildJobWithAi} disabled={!aiJobText.trim() || aiJobBusy}
                  className="flex-1 py-2.5 rounded-xl bg-purple-600 text-white font-semibold text-sm hover:bg-purple-700 disabled:opacity-40 flex items-center justify-center gap-2">
                  {aiJobBusy ? <><span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block" /> Building… (~30s)</> : <>Build the job</>}
                </button>
                <button onClick={() => setMode('choose')} className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-500 text-sm hover:bg-gray-100">Back</button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/40">
      <div className="bg-white w-full sm:rounded-2xl shadow-2xl sm:max-w-lg max-h-[92vh] flex flex-col rounded-t-2xl">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-black uppercase tracking-tight text-gray-900">
              {form.is_recurring ? 'Schedule Recurring Job' : 'New Job'}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {new Date(form.scheduled_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-500 hover:text-gray-600 hover:bg-gray-100 rounded-full">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

          {/* ── Date & Time ── */}
          <Section icon={<Calendar className="h-4 w-4" />} label="When">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-sm">Date *</label>
                <input type="date" value={form.scheduled_date} onChange={f('scheduled_date')} className="input" />
              </div>
              <div>
                <label className="label-sm">Time</label>
                <div className="grid grid-cols-2 gap-1">
                  <button type="button" onClick={() => { setTimeOpen(false); setForm(p => ({ ...p, time_start: '', time_is_firm: false })); }}
                    className={`py-2 rounded-lg border text-xs font-semibold ${!form.time_start && !timeOpen ? 'bg-brand-50 border-brand-400 text-brand-800' : 'border-gray-300 text-gray-600'}`}>Anytime</button>
                  <button type="button" onClick={() => setTimeOpen(true)}
                    className={`py-2 rounded-lg border text-xs font-semibold ${form.time_start || timeOpen ? 'bg-brand-50 border-brand-400 text-brand-800' : 'border-gray-300 text-gray-600'}`}>Set a time</button>
                </div>
              </div>
            </div>
            {(form.time_start || timeOpen) ? (
              <>
                <input type="time" value={form.time_start} onChange={f('time_start')} autoFocus={timeOpen && !form.time_start}
                  className={`input mt-2 ${form.time_is_firm ? 'border-red-400 text-red-700 font-bold' : ''}`} />
                <label className={`mt-2 flex items-center gap-2 text-sm rounded-lg px-3 py-2 border cursor-pointer ${form.time_is_firm ? 'bg-red-50 border-red-300 text-red-800' : 'border-gray-200 text-gray-600'}`}>
                  <input type="checkbox" checked={form.time_is_firm} onChange={e => setForm(p => ({ ...p, time_is_firm: e.target.checked }))} className="rounded border-gray-300 text-red-600" />
                  <span><span className="font-semibold">⏰ Must start at this exact time</span> <span className="text-xs opacity-70">— Optimize keeps it; otherwise it's a rough time</span></span>
                </label>
              </>
            ) : (
              <p className="mt-2 text-xs text-gray-500">Anytime that day — no set time. Optimize fits it in around the stops that have one.</p>
            )}
            {!form.is_recurring && !continueOf && (
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-700">
                <span>Takes</span>
                <select value={spanDays} onChange={e => setSpanDays(Number(e.target.value))} className="input w-auto py-1 text-sm">
                  {[1, 2, 3, 4, 5, 6, 7].map(n => <option key={n} value={n}>{n} day{n === 1 ? '' : 's'}</option>)}
                </select>
                {spanDays > 1 && <span className="text-xs text-gray-500">one linked job per day (weekends skipped), billed once on day 1</span>}
              </div>
            )}
          </Section>

          {/* ── Service Type ── */}
          <Section icon={<Tag className="h-4 w-4" />} label="Service">
            <ServiceTagPicker
              primary={form.service_type}
              tags={form.service_tags}
              onChange={(primary, tags) => setForm(p => ({ ...p, service_type: primary, service_tags: tags }))}
            />
            {oneOffNeedsBid && (
              <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5">
                <p className="text-sm font-semibold text-amber-900">One-off work needs a written scope and the customer's yes — make a bid first.</p>
                <p className="text-xs text-amber-800 mt-0.5">Write the scope on the bid and send it; once they sign it (on the bid page, or by texting "I accept" or YES), "Make the job" on the bid puts it on the calendar{recentBids.length ? ' — or open one of their bids above' : ''}.</p>
                <button type="button" onClick={() => { onClose(); navigate('/quotes'); }} className="mt-1.5 text-sm font-semibold text-brand-700 underline">Make a bid →</button>
              </div>
            )}

            {/* Title */}
            <div className="mt-3">
              <label className="label-sm">Job Title <span className="text-gray-500 font-normal">(optional)</span></label>
              <input
                value={form.title}
                onChange={f('title')}
                className="input"
                placeholder={currentServiceOpt ? `e.g. ${currentServiceOpt.emoji} ${currentServiceOpt.label}` : 'Job title…'}
              />
            </div>

          </Section>

          {/* ── Client ── */}
          <Section icon={<MapPin className="h-4 w-4" />} label="Client *">
            {form.customer_id && !showAddCustomer ? (
              /* Selected customer chip */
              <div className="flex items-center gap-2">
                <div className="flex-1 flex items-center gap-2.5 bg-brand-50 border border-brand-200 rounded-xl px-3 py-2.5">
                  <CheckCircle className="h-4 w-4 text-brand-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-brand-900 truncate">
                      {selectedCustomer?.display_name || selectedCustomer?.name || ''}
                      <AccountMark mark={selectedCustomer?.account_mark} />
                    </p>
                    {selectedProp?.address && (
                      <p className="text-xs text-brand-600 truncate">{selectedProp.address}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => { setForm(p => ({ ...p, customer_id: '', property_id: '' })); setCustomerSearch(''); setTimeout(() => searchRef.current?.focus(), 50); }}
                    className="text-xs text-brand-500 hover:text-brand-700 font-medium flex-shrink-0"
                  >
                    Change
                  </button>
                </div>
              </div>
            ) : (
              /* Search + add-customer flow */
              <div>
                {!showAddCustomer && (
                  <div className="relative">
                    <input
                      ref={searchRef}
                      type="text"
                      className="input"
                      placeholder="Search clients or addresses…"
                      value={customerSearch}
                      autoFocus
                      onChange={e => { setCustomerSearch(e.target.value); setShowCustomerDrop(true); }}
                      onFocus={() => setShowCustomerDrop(true)}
                      onBlur={() => setTimeout(() => setShowCustomerDrop(false), 150)}
                    />

                    {showCustomerDrop && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl max-h-52 overflow-y-auto">
                        {filteredCustomers.slice(0, 20).map((c: any) => (
                          <button key={c.id} type="button"
                            onMouseDown={() => selectCustomer(c)}
                            className="w-full text-left px-3 py-2.5 text-sm hover:bg-brand-50 hover:text-brand-900 border-b border-gray-50 last:border-0 flex items-center gap-2">
                            <span className="flex-1 font-medium truncate">{c.display_name || c.name}<AccountMark mark={c.account_mark} /></span>
                            {c.company_name && c.name !== c.company_name && (
                              <span className="text-xs text-gray-500 flex-shrink-0">{c.name}</span>
                            )}
                          </button>
                        ))}

                        {propMatches.length > 0 && (
                          <>
                            <p className="px-3 pt-2 pb-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Properties</p>
                            {propMatches.map((p: any) => (
                              <button key={`p${p.id}`} type="button"
                                onMouseDown={() => selectProperty(p)}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-brand-50 border-b border-gray-50 last:border-0">
                                <span className="font-medium text-gray-800">{p.address}</span>{p.city ? <span className="text-gray-500">, {p.city}</span> : null}
                                <span className="block text-xs text-gray-500">{p.customer_name}{p.label && !/^main property$/i.test(p.label) ? ` · ${p.label}` : ''}</span>
                              </button>
                            ))}
                          </>
                        )}

                        {/* No match — show Add Customer option */}
                        {noMatch && (
                          <button
                            type="button"
                            onMouseDown={() => { setShowAddCustomer(true); setShowCustomerDrop(false); }}
                            className="w-full text-left px-3 py-2.5 flex items-center gap-2 text-brand-700 font-semibold text-sm bg-brand-50 hover:bg-brand-100 border-t border-brand-100"
                          >
                            <UserPlus className="h-4 w-4 flex-shrink-0" />
                            Add "{customerSearch.trim()}" as new client
                          </button>
                        )}

                        {filteredCustomers.length === 0 && !noMatch && (
                          <p className="px-3 py-3 text-xs text-gray-500 text-center">Type to search…</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Inline add-customer form */}
                {showAddCustomer && (
                  <AddCustomerInline
                    prefillName={customerSearch.trim()}
                    onAdded={handleCustomerAdded}
                    onCancel={() => setShowAddCustomer(false)}
                  />
                )}

                {/* No-match hint below the box when dropdown closed */}
                {!showAddCustomer && noMatch && !showCustomerDrop && (
                  <button
                    type="button"
                    onClick={() => setShowAddCustomer(true)}
                    className="mt-1.5 w-full flex items-center gap-2 text-sm text-brand-700 font-medium px-3 py-2 rounded-xl border border-brand-200 bg-brand-50 hover:bg-brand-100"
                  >
                    <UserPlus className="h-4 w-4" />
                    Add "{customerSearch.trim()}" as new client
                  </button>
                )}
              </div>
            )}

            {/* Day 2 of an earlier job? */}
            {continueOf && (
              <div className="mt-2 flex items-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-900">
                <span className="font-semibold">Day 2 of #{continueOf.id}</span>
                <span className="text-indigo-700">{String(continueOf.scheduled_date).slice(0, 10)} · {continueOf.title || continueOf.service_type} · billed there</span>
                <button type="button" onClick={() => { setContinueOf(null); setContinueDismissed(true); }} className="ml-auto text-indigo-500 hover:text-indigo-800" title="Not a continuation"><X className="h-3.5 w-3.5" /></button>
              </div>
            )}
            {recentBids.length > 0 && (
              <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
                <p className="text-xs font-semibold text-amber-900">{recentBids.length === 1 ? 'They already have a bid' : 'They already have bids'}</p>
                <p className="text-[11px] text-amber-800 mb-1.5">Is this job that work? Book it from the bid and its lines, price and deposit come with it{recentBids.some((q: any) => q.offer?.why === 'project') ? ' — a bid that is project work goes on a day from its bid page' : ''}.</p>
                <div className="space-y-1">
                  {recentBids.map((q: any) => (
                    <button key={q.id} type="button" onClick={() => { onClose(); navigate(`/quotes?open=${q.id}`); }}
                      className="w-full text-left flex items-center gap-2 rounded-lg bg-white border border-amber-200 px-2.5 py-1.5 text-xs hover:border-amber-400">
                      <span className="font-semibold text-gray-800 truncate">{q.title || `Bid #${q.id}`}</span>
                      {Number(q.total) > 0 && <span className="text-gray-600">${Number(q.total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
                      {(() => { const b = bidBadge(q); return <span className={`px-1.5 py-0.5 rounded-full text-[10px] whitespace-nowrap ${b.cls}`}>{b.words}</span>; })()}
                      {q.twin && <span className="text-[10px] text-gray-500 whitespace-nowrap" title="This bid sits on another customer row with the same name or phone">on their other row</span>}
                      <span className="ml-auto text-amber-800 font-semibold whitespace-nowrap">{q.offer?.why === 'project' ? 'Make the job →' : 'Open the bid →'}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {!continueOf && continueCandidates.length > 0 && (
              <div className="mt-2 rounded-xl border border-indigo-200 bg-indigo-50/70 px-3 py-2.5">
                <p className="text-xs font-semibold text-indigo-900">Same job, another day?</p>
                <p className="text-[11px] text-indigo-700 mb-1.5">Recent work for this client — tap one if today continues it.</p>
                <div className="space-y-1">
                  {continueCandidates.map((j: any) => (
                    <button key={j.id} type="button" onClick={() => pickContinuation(j)}
                      className="w-full text-left flex items-center gap-2 rounded-lg bg-white border border-indigo-200 px-2.5 py-1.5 text-xs hover:border-indigo-400">
                      <span className="font-semibold text-gray-800 capitalize">{j.title || String(j.service_type || 'job').replace(/_/g, ' ')}</span>
                      <span className="text-gray-500">{new Date(String(j.scheduled_date).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                      <span className={`ml-auto px-1.5 py-0.5 rounded-full text-[10px] ${j.status === 'complete' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{j.is_partial ? 'partial' : j.status.replace('_', ' ')}</span>
                      <span className="text-indigo-700 font-semibold">Yes, day 2 →</span>
                    </button>
                  ))}
                </div>
                <button type="button" onClick={() => setContinueDismissed(true)} className="mt-1.5 text-[11px] text-indigo-500 hover:text-indigo-800">No — this is new work</button>
              </div>
            )}

            {/* Property picker */}
            {properties.length > 0 && (
              <div className="mt-3">
                <label className="label-sm">Property</label>
                <select value={form.property_id} onChange={f('property_id')} className="input">
                  <option value="">Select property…</option>
                  {properties.map((p: any) => {
                    const addr = [p.address, p.city, p.state].filter(Boolean).join(', ');
                    return <option key={p.id} value={p.id}>{p.label && !/^main property$/i.test(p.label) ? `${p.label} — ${addr}` : addr}</option>;
                  })}
                </select>
                {selectedProp && (
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    {isMowing && selectedProp.mowing_price && (
                      <span className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-2 py-0.5 font-semibold">
                        <DollarSign className="h-3 w-3" />{selectedProp.mowing_price}/mow
                      </span>
                    )}
                    {selectedProp.estimated_minutes && (
                      <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5">
                        <Clock className="h-3 w-3" />{selectedProp.estimated_minutes} min est.
                      </span>
                    )}
                    {isMowing && selectedProp.cutting_height && (
                      <span className="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
                        Cut: {selectedProp.cutting_height}"
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </Section>

          {/* ── Route — mowing only. Bush jobs, mulch, irrigation are not route stops. ── */}
          {isMowing && activeRoutes.length > 0 && (
            <Section icon={<Zap className="h-4 w-4" />} label="Route">
              {/* Something is already out that day — offer it rather than making
                  them remember which route runs on a Thursday. */}
              {!form.route_id && routesThatDay.length > 0 && (
                <div className="mb-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2.5">
                  <p className="text-sm text-brand-900">
                    {routesThatDay.length === 1
                      ? <><b>{routesThatDay[0].name}</b> is already out that day — {routesThatDay[0].stops} stop{routesThatDay[0].stops === 1 ? '' : 's'}.</>
                      : <>{routesThatDay.length} routes are out that day.</>}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {routesThatDay.map(r => (
                      <button key={r.id} type="button"
                        onClick={() => { setForm(p => ({ ...p, route_id: String(r.id) })); setRouteCadence(cadenceOfRoute(r.id)); }}
                        className="press px-2.5 py-1.5 rounded-xl bg-brand-600 text-black text-xs font-semibold">
                        Add to {r.name} ({r.stops})
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <select value={form.route_id} onChange={e => { f('route_id')(e); if (!e.target.value) setRouteCadence(''); }} className="input">
                <option value="">— One-off / no route —</option>
                {activeRoutes.map((r: any) => (
                  <option key={r.id} value={r.id}>
                    {r.name}{routesThatDay.some(x => x.id === r.id) ? ' — runs that day' : ''}
                  </option>
                ))}
              </select>

              {form.route_id && pickedRouteOffered && (
                <p className="text-[11px] text-gray-500 mt-1.5">This route is offered as an open shift for one day. The visit is booked once and goes with whoever claims the day.</p>
              )}
              {/* On a route means it comes round again. How often? */}
              {form.route_id && !pickedRouteOffered && (
                <div className="mt-2">
                  <p className="text-xs font-medium text-gray-600 mb-1.5">How often does this one get cut?</p>
                  <div className="flex flex-wrap gap-1.5">
                    {([['weekly', 'Weekly'], ['biweekly', 'Every 2 weeks'], ['monthly', 'Monthly'], ['', 'Just this once']] as const).map(([val, lbl]) => (
                      <button key={val || 'once'} type="button" onClick={() => setRouteCadence(val)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                          routeCadence === val ? 'bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-700 border-gray-300'}`}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                  {routeCadence && (pickedRouteOut === false && routeCadence !== 'weekly' ? (
                    <p className="text-[11px] text-amber-700 mt-1.5">
                      This route is not out on {niceDay(form.scheduled_date)}. This visit is booked as a one-off that day, and the repeat starts on the route's next run{pickedRouteNext ? `: ${niceDay(pickedRouteNext)}` : ''}.
                    </p>
                  ) : (
                    <p className="text-[11px] text-gray-500 mt-1.5">
                      This visit is booked for {niceDay(form.scheduled_date)}, and it repeats from there.
                    </p>
                  ))}
                </div>
              )}
            </Section>
          )}

          {/* ── Pricing ── */}
          <Section icon={<DollarSign className="h-4 w-4" />} label="Pricing">
            <div className="flex items-center justify-between mb-1">
              {isMowing && (
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={form.is_first_time}
                    onChange={e => setForm(p => ({ ...p, is_first_time: e.target.checked }))}
                    className="rounded text-brand-600 focus:ring-brand-500" />
                  First visit (crew gets 60%)
                </label>
              )}
              {!isMowing && <span />}
              {/* Est. minutes for crew pay calc */}
              {!form.is_first_time && (
                <div className="flex items-center gap-1.5">
                  <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">Est. min</label>
                  <input type="text" inputMode="numeric"
                    value={form.minutes_override} onChange={e => setForm(p => ({ ...p, minutes_override: e.target.value.replace(/[^0-9]/g, '') }))}
                    placeholder={selectedProp?.estimated_minutes ? String(selectedProp.estimated_minutes) : '—'}
                    className="input w-20 py-1 text-sm text-center" />
                </div>
              )}
            </div>

            {/* Line items — these ARE the price. A mow does not have them: what a
                yard costs to cut is the price on the property, and putting a
                quote builder on a $45 stop is a way to get $45 wrong. */}
            {isMowing ? (
              <p className="text-xs text-gray-500">
                {selectedProp?.mowing_price
                  ? <>Charged at the yard's mowing price, <b>${Number(selectedProp.mowing_price).toFixed(2)}</b>. Change it on the property to change it everywhere.</>
                  : (
                    <span className="flex flex-wrap items-center gap-2 text-gray-700">
                      <span className="font-medium">This yard has no mowing price yet. Price per mow: $</span>
                      <input type="number" min={0} step="1" inputMode="decimal" value={yardPrice} onChange={e => setYardPrice(e.target.value)} placeholder="45"
                        className={`input w-24 py-1 text-sm ${yardPrice === '' ? 'border-amber-400' : ''}`} aria-label="Mowing price for this yard" />
                      <span className="text-gray-500">{form.property_id ? 'Saved on the yard, so every mow uses it.' : 'Goes on this job.'}{yardPrice === '' ? ' Leave it blank and the job is still made, with no price on it.' : ''}</span>
                    </span>
                  )}
              </p>
            ) : (
            <div>
              {lineItems.length > 0 && (
                <div className="mb-2">
                  {/* Column headers */}
                  <div className="grid grid-cols-[1fr_52px_76px_52px_28px] gap-1.5 text-[10px] font-medium text-gray-500 uppercase tracking-wide px-0.5 mb-1">
                    <span>Description</span>
                    <span className="text-center">Qty</span>
                    <span className="text-center">Unit $</span>
                    <span className="text-right">Total</span>
                    <span />
                  </div>
                  {/* Rows */}
                  {lineItems.map((li, idx) => (
                    <div key={idx} className="grid grid-cols-[1fr_52px_76px_52px_28px] gap-1.5 items-center mb-1.5">
                      <div className="relative">
                        <input value={li.description} onChange={e => updateLineItem(idx, 'description', e.target.value)}
                          placeholder="Description" className="input py-1.5 text-sm pr-7 w-full" />
                        <button type="button" title="AI expand"
                          onClick={() => generateAiDescription(idx)} disabled={aiLoadingIdx === idx}
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-purple-400 hover:text-purple-600 disabled:opacity-40">
                          <Sparkles className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      {/* Text + decimal keypad rather than type="number": no spinner arrows
                          (spec), and a scroll wheel can't nudge a price. Anything but digits
                          and a point is dropped, so parseFloat below reads it the same. */}
                      <input value={li.quantity} onChange={e => updateLineItem(idx, 'quantity', e.target.value.replace(/[^0-9.]/g, ''))}
                        type="text" inputMode="decimal"
                        className="input py-1.5 text-sm text-center" />
                      <input value={li.unit_price} onChange={e => updateLineItem(idx, 'unit_price', e.target.value.replace(/[^0-9.]/g, ''))}
                        type="text" inputMode="decimal" placeholder="0.00"
                        className="input py-1.5 text-sm text-right" />
                      <span className="text-xs font-semibold text-gray-700 text-right pr-0.5 tabular-nums">
                        ${((parseFloat(li.unit_price) || 0) * (parseFloat(li.quantity) || 1)).toFixed(2)}
                      </span>
                      <button type="button" onClick={() => removeLineItem(idx)}
                        className="text-gray-300 hover:text-red-500 flex items-center justify-center">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  {/* Total row */}
                  <div className="flex justify-end items-center gap-2 border-t border-gray-100 pt-2 mt-1">
                    <span className="text-xs text-gray-500">Total</span>
                    <span className="text-base font-bold text-gray-900">${lineItemsTotal.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Add buttons */}
              <div className="flex flex-wrap gap-1.5 mt-1">
                <div className="relative">
                  <button type="button"
                    onClick={() => setShowLineItemDropdown(v => !v)}
                    onBlur={() => setTimeout(() => setShowLineItemDropdown(false), 150)}
                    className="text-xs text-brand-600 hover:text-brand-800 border border-brand-200 rounded-lg px-2.5 py-1.5 flex items-center gap-1 bg-white">
                    <ChevronDown className="h-3 w-3" /> Presets
                  </button>
                  {showLineItemDropdown && (
                    <div className="absolute left-0 top-full z-20 mt-1 w-52 bg-white border border-gray-200 rounded-xl shadow-xl max-h-52 overflow-y-auto">
                      {(catalogData as any[]).filter((c: any) => c.is_active !== 0).map((c: any) => (
                        <button key={c.id} type="button"
                          onMouseDown={() => { addLineItem(c); setShowLineItemDropdown(false); }}
                          className="w-full text-left px-3 py-2 text-xs hover:bg-brand-50 border-b border-gray-50">
                          <div className="font-medium">{c.name}</div>
                          {c.default_unit_price ? <div className="text-gray-500">${c.default_unit_price}</div> : null}
                        </button>
                      ))}
                      {COMMON_LINE_ITEMS.map(item => (
                        <button key={item.name} type="button"
                          onMouseDown={() => { addLineItem(item); setShowLineItemDropdown(false); }}
                          className="w-full text-left px-3 py-2 text-xs hover:bg-brand-50 border-b border-gray-50 last:border-0">
                          {item.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button type="button" onClick={() => addLineItem()}
                  className="text-xs text-brand-600 hover:text-brand-800 border border-brand-200 rounded-lg px-2.5 py-1.5 flex items-center gap-1 bg-white">
                  <Plus className="h-3 w-3" /> Add line item
                </button>
                <button type="button" onClick={() => setShowAiTextInput(v => !v)}
                  className="text-xs text-purple-600 hover:text-purple-800 border border-purple-200 rounded-lg px-2.5 py-1.5 flex items-center gap-1 bg-white">
                  <Wand2 className="h-3 w-3" /> AI from text
                </button>
              </div>

              {/* AI from text inline input */}
              {showAiTextInput && (
                <div className="mt-2 p-2.5 border border-purple-200 rounded-xl bg-purple-50/50">
                  <p className="text-[10px] text-purple-600 font-medium mb-1.5">
                    Paste your raw notes — AI builds a full itemized quote (sections, FREE lines, either/or pricing, totals)
                  </p>
                  <textarea
                    autoFocus
                    value={aiTextInput}
                    onChange={e => setAiTextInput(e.target.value)}
                    placeholder={"station 1 replace 12in riser 35, cleaned filter free, station 5 hunter valve low pressure — diaphragm 95 or whole valve 185, capped bed head 25 or bubbler for roses 120…"}
                    rows={4}
                    className="input text-sm w-full resize-none"
                  />
                  <div className="flex gap-2 mt-1.5">
                    <button type="button" onClick={applyAiText}
                      disabled={!aiTextInput.trim() || aiParsing}
                      className="text-xs px-3 py-1.5 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 disabled:opacity-40 flex items-center gap-1.5">
                      {aiParsing ? <><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" /> Building quote…</> : 'Build itemized quote'}
                    </button>
                    <button type="button" onClick={() => { setShowAiTextInput(false); setAiTextInput(''); setAiQuoteText(''); }}
                      className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100">
                      Cancel
                    </button>
                  </div>

                  {/* Customer-ready quote text */}
                  {aiQuoteText && (
                    <div className="mt-2.5 border border-purple-300 rounded-lg bg-white">
                      <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-purple-100">
                        <p className="text-[10px] font-bold text-purple-700 uppercase tracking-wide">Quote text — ready to send</p>
                        <button type="button"
                          onClick={() => { navigator.clipboard.writeText(aiQuoteText).then(() => toast.success('Copied — paste it into a text to the customer')); }}
                          className="text-[11px] px-2 py-1 rounded bg-purple-600 text-white font-semibold hover:bg-purple-700">
                          Copy
                        </button>
                      </div>
                      <pre className="px-2.5 py-2 text-[11px] text-gray-800 whitespace-pre-wrap font-sans max-h-48 overflow-y-auto">{aiQuoteText}</pre>
                    </div>
                  )}
                </div>
              )}
            </div>
            )}
          </Section>

          {/* ── Crew notes + flat pay ── */}
          <Section icon={<FileText className="h-4 w-4" />} label="For the crew">
            <label className="label-sm">Crew notes <span className="text-gray-500 font-normal">— shown on their phone at the stop (no prices)</span></label>
            <textarea
              value={form.crew_notes}
              onChange={f('crew_notes')}
              rows={2}
              className="input"
              placeholder="Gate code 4482 · push mow only · leaves go in the side bin…"
            />
            {/* Flat pay is a project thing. A mow's pay comes off the estimate,
                which is the rule the whole pay ledger is built on. */}
            {!isMowing && (
              <div className="mt-3">
                <label className="label-sm">Flat crew pay <span className="text-gray-500 font-normal">(optional — projects; overrides hourly estimate)</span></label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                  <input type="text" inputMode="decimal" value={form.flat_crew_pay} onChange={e => setForm(p => ({ ...p, flat_crew_pay: e.target.value.replace(/[^0-9.]/g, '') }))} className="input pl-7" placeholder="e.g. 150" />
                </div>
              </div>
            )}
          </Section>

          {/* ── Assign employees — not for a mow. Who cuts a route stop is
                 decided by who is on the route that day, not here. ── */}
          {!isMowing && (
          <Section icon={<Users className="h-4 w-4" />} label="Assign employees">
            <div className="flex flex-wrap gap-1.5">
              {[...(employeesData as any[])].sort(byRecentAssignment).map((emp: any) => {
                const on = crewIds.includes(emp.id);
                return (
                  <button key={emp.id} type="button"
                    onClick={() => setCrewIds(prev => on ? prev.filter(id => id !== emp.id) : [...prev, emp.id])}
                    className={`press px-2.5 py-1 text-xs rounded-full border font-medium ${on ? 'bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-600 border-gray-200 hover:border-brand-400'}`}
                    title={emp.last_assigned_date ? `last worked ${fmtLastWorked(emp.last_assigned_date)}` : 'never assigned yet'}>
                    {emp.name}
                  </button>
                );
              })}
              {(employeesData as any[]).length === 0 && <span className="text-xs text-gray-500">No active employees.</span>}
            </div>
            <p className="text-[11px] text-gray-500 mt-1.5">
              {crewIds.length ? `${crewIds.length} picked — they'll see it on their phones.` : 'Optional — recent people first. Leave empty to use the route crew.'}
              {form.is_recurring ? ' Applies to every visit of the schedule.' : ''}
            </p>
          </Section>
          )}

          {/* ── Notes ── */}
          <Section icon={<FileText className="h-4 w-4" />} label="Office notes">
            <textarea
              value={form.notes}
              onChange={f('notes')}
              className="input"
              rows={2}
              placeholder="Internal only — the crew never sees this. Field instructions go in Crew notes above."
            />
          </Section>

          {/* ── More options: Recurring + Open Shift ── */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            {/* Recurring toggle row */}
            <button
              type="button"
              onClick={() => { setShowRecurring(v => !v); setForm(p => ({ ...p, is_recurring: !showRecurring })); }}
              className="w-full flex items-center gap-2.5 px-4 py-3 hover:bg-gray-50 text-left border-b border-gray-100 last:border-0"
            >
              <RefreshCw className={`h-4 w-4 flex-shrink-0 ${showRecurring ? 'text-brand-600' : 'text-gray-500'}`} />
              <span className={`text-sm font-medium flex-1 ${showRecurring ? 'text-brand-700' : 'text-gray-700'}`}>
                Recurring schedule
              </span>
              {showRecurring && freqSummary() && (
                <span className="text-xs text-brand-500 font-medium mr-1">{freqSummary()}</span>
              )}
              <ChevronDown className={`h-4 w-4 text-gray-500 flex-shrink-0 transition-transform ${showRecurring ? '' : '-rotate-90'}`} />
            </button>

            {showRecurring && (
              <div className="px-4 py-3 bg-gray-50 space-y-3 border-b border-gray-100">
                <div>
                  <label className="label-sm">Frequency</label>
                  <div className="flex gap-2">
                    {([['weekly','Weekly'],['biweekly','Every 2 wks'],['monthly','Monthly'],['custom','Custom']] as const).map(([val, lbl]) => {
                      const monthly = form.frequency === 'custom' && !customOpen && Number(form.custom_interval_days) === 28;
                      const on = val === 'monthly' ? monthly : val === 'custom' ? form.frequency === 'custom' && !monthly : form.frequency === val;
                      return (
                        <button key={val} type="button"
                          onClick={() => {
                            if (val === 'monthly') { setCustomOpen(false); setForm(p => ({ ...p, frequency: 'custom' as any, custom_interval_days: 28 })); }
                            else { setCustomOpen(val === 'custom'); setForm(p => ({ ...p, frequency: val as any })); }
                          }}
                          className={`flex-1 py-1.5 text-xs rounded-lg border font-medium transition-colors ${
                            on
                              ? 'bg-brand-600 text-black border-brand-600'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-brand-400'
                          }`}>
                          {lbl}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {form.frequency === 'custom' && !customOpen && Number(form.custom_interval_days) === 28 && (
                  <p className="text-xs text-gray-600">Every 4 weeks, on the weekday of the first visit.</p>
                )}

                {form.frequency === 'custom' && (customOpen || Number(form.custom_interval_days) !== 28) && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">Every</span>
                    <input type="number" min={1} max={365} value={form.custom_interval_days}
                      onChange={e => setForm(p => ({ ...p, custom_interval_days: Math.max(1, Number(e.target.value)) }))}
                      className="input w-20 text-center" />
                    <span className="text-sm text-gray-700">days</span>
                  </div>
                )}

                {form.frequency !== 'custom' && (
                  <div>
                    <label className="label-sm">Day(s)</label>
                    <div className="flex gap-1.5 flex-wrap">
                      {DAYS.map((d, i) => {
                        const disabled = i === 0 && avoidSundays;
                        const checked  = form.selected_days.includes(i);
                        return (
                          <button key={d} type="button" disabled={disabled}
                            onClick={() => !disabled && setForm(p => ({
                              ...p,
                              selected_days: checked
                                ? p.selected_days.filter(x => x !== i)
                                : [...p.selected_days, i],
                            }))}
                            className={`px-2.5 py-1 text-xs rounded-full border font-medium transition-colors ${
                              disabled ? 'opacity-30 cursor-not-allowed bg-white text-gray-500 border-gray-200'
                              : checked  ? 'bg-brand-600 text-black border-brand-600'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-brand-400'
                            }`}>
                            {d}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div>
                  <label className="label-sm">Ends</label>
                  <div className="space-y-1.5">
                    {(['never','date','count'] as const).map(opt => (
                      <label key={opt} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input type="radio" name="end_type" checked={form.end_type === opt}
                          onChange={() => setForm(p => ({ ...p, end_type: opt }))}
                          className="text-brand-600 focus:ring-brand-500" />
                        {opt === 'never' ? 'Never' : opt === 'date' ? 'On date:' : 'After:'}
                        {opt === 'date' && form.end_type === 'date' && (
                          <input type="date" value={form.end_date}
                            onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))}
                            className="input py-0.5 px-2 text-sm w-auto" />
                        )}
                        {opt === 'count' && form.end_type === 'count' && (
                          <>
                            <input type="number" min={1} max={200} value={form.end_after_occurrences}
                              onChange={e => setForm(p => ({ ...p, end_after_occurrences: Number(e.target.value) }))}
                              className="input py-0.5 px-2 text-sm w-16 text-center" />
                            <span className="text-gray-500">times</span>
                          </>
                        )}
                      </label>
                    ))}
                  </div>
                </div>

                {freqSummary() && (
                  <p className="text-xs text-brand-600 bg-brand-50 rounded-lg px-2 py-1">
                    📅 {freqSummary()}{form.service_type === 'mowing' ? ' · Mowing season' : ''}
                  </p>
                )}
              </div>
            )}

            {/* Open shift — a project thing. A route stop is already covered by
                whoever is running the route, so there is nothing to claim. A
                repeating series books every visit closed, so not there either. */}
            {openShiftAllowed && (
            <>
            <button
              type="button"
              onClick={() => { setShowOpenShift(v => !v); setForm(p => ({ ...p, is_open_shift: !showOpenShift })); }}
              className="w-full flex items-center gap-2.5 px-4 py-3 hover:bg-gray-50 text-left"
            >
              <Users className={`h-4 w-4 flex-shrink-0 ${showOpenShift ? 'text-orange-500' : 'text-gray-500'}`} />
              <span className={`text-sm font-medium flex-1 ${showOpenShift ? 'text-orange-700' : 'text-gray-700'}`}>
                Open shift — crew can claim
              </span>
              <ChevronDown className={`h-4 w-4 text-gray-500 flex-shrink-0 transition-transform ${showOpenShift ? '' : '-rotate-90'}`} />
            </button>

            {showOpenShift && (
              <div className="px-4 py-3 bg-orange-50 space-y-2">
                <div className="flex items-center gap-3">
                  <label className="text-sm text-gray-600">People needed:</label>
                  <input type="number" min={1} max={20}
                    value={form.open_shift_required_count}
                    onChange={e => setForm(p => ({ ...p, open_shift_required_count: Math.max(1, Number(e.target.value)) }))}
                    className="input w-20 text-center py-1" />
                </div>
                <div>
                  <label className="label-sm">Who can claim it</label>
                  <div className="space-y-1">
                    {([['everyone', 'Anyone on the crew app'], ['picked', 'Only people I pick']] as const).map(([val, lbl]) => (
                      <label key={val} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input type="radio" name="open_shift_to" checked={shiftTo === val}
                          onChange={() => setShiftTo(val)}
                          className="text-brand-600 focus:ring-brand-500" />
                        {lbl}
                      </label>
                    ))}
                  </div>
                </div>
                {shiftTo === 'picked' && (
                  <div className="bg-white border border-orange-200 rounded-lg p-2.5">
                    <PeoplePicker value={shiftPicks} onChange={setShiftPicks} />
                    {shiftPicks.length === 0 ? (
                      <p className="text-xs text-amber-700 mt-1.5">Pick at least one person.</p>
                    ) : shiftPicks.length < (form.open_shift_required_count || 1) ? (
                      <p className="text-xs text-amber-700 mt-1.5">
                        {shiftPicks.length} picked for {form.open_shift_required_count} needed — it can't fill unless you add more.
                      </p>
                    ) : null}
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={textShiftNow} onChange={e => setTextShiftNow(e.target.checked)}
                    className="rounded border-gray-300 text-brand-600" />
                  Text them now
                </label>
              </div>
            )}
            </>
            )}
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="px-5 py-4 border-t border-gray-100 flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-shrink-0">
            Cancel
          </button>
          <button
            onClick={() => create.mutate()}
            disabled={create.isLoading || !form.customer_id || !form.scheduled_date || shiftNeedsPeople || oneOffNeedsBid}
            className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-40"
          >
            {create.isLoading ? (
              'Creating…'
            ) : oneOffNeedsBid ? (
              'Make a bid first'
            ) : showRecurring ? (
              <><RefreshCw className="h-4 w-4" /> Schedule Recurring</>
            ) : (
              <><Plus className="h-4 w-4" /> Create Job</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────────
function Section({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        <span className="text-gray-500">{icon}</span>
        <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">{label}</span>
      </div>
      {children}
    </div>
  );
}
